import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  useColorModeValue,
  VStack,
  useDisclosure,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  useBreakpointValue,
  Text,
  Button,
  Divider,
  HStack,
  Tag,
  Wrap,
  WrapItem,
  TagLabel,
  TagCloseButton,
  Badge,
  Icon,
  Tooltip,
  SimpleGrid,
  ButtonGroup,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  IconButton
} from '@chakra-ui/react';
import { useAuth } from '../../hooks/useAuth';
import { updateMediaFeatures } from '../../firebase/siteMedia';
import MediaGalleryHeader from './MediaGalleryHeader';
import MediaGalleryGrid from './MediaGalleryGrid';
import MediaGalleryUpload from './MediaGalleryUpload';
import MobileMediaFilters from './MobileMediaFilters';
import ImagePreviewModal from '../ImagePreviewModal';
import VideoModal from './VideoModal';
import ArticleModal from './ArticleModal';
import ThreeDViewerModal from '../ThreeDViewerModal';
import { Images, FunnelSimple, X, CheckCircle, Info as InfoIcon, Plus } from '@phosphor-icons/react';
import { mediaHasFeature, normalizeFeature, mapFeature, getMediaFeatures } from '../../utils/featureUtils';

// Get unique features from all media
const getAllFeatures = (mediaArray) => {
  if (!Array.isArray(mediaArray)) return [];
  
  const featuresMap = new Map();
  
  mediaArray.forEach(media => {
    if (!media?.predictions?.length) return;
    
    // Get normalized features for this media
    const mediaFeatures = getMediaFeatures(media);
    
    mediaFeatures.forEach(feature => {
      const normalizedId = normalizeFeature(feature.id);
      
      if (!featuresMap.has(normalizedId)) {
        // Create new entry
        featuresMap.set(normalizedId, {
          id: normalizedId,
          label: feature.label,
          source: feature.source,
          count: 1
        });
      } else {
        // Update existing entry count
        featuresMap.get(normalizedId).count++;
      }
    });
  });
  
  return Array.from(featuresMap.values())
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

export default function MediaGallery({
  site,
  onMediaUploadComplete,
  onFeaturesUpdated,
  onMediaUpdated,
  filters,
  filteredMedia,
  media,
  onFiltersChange
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [showThumbnailTags, setShowThumbnailTags] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [activeTag, setActiveTag] = useState(null);
  const [currentMediaType, setCurrentMediaType] = useState('all');
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);
  const [isThreeDModalOpen, setIsThreeDModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const { user } = useAuth();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 20; // Increased number of items per page for better grid display
  
  // Listen for custom featureFilter events from modals
  useEffect(() => {
    const handleFeatureFilterEvent = (event) => {
      if (event.detail && event.detail.featureId) {
        handleTagClick(event.detail.featureId);
      }
    };
    
    document.addEventListener('featureFilter', handleFeatureFilterEvent);
    
    return () => {
      document.removeEventListener('featureFilter', handleFeatureFilterEvent);
    };
  }, [selectedTags]); // Dependency on selectedTags to get current state
  
  // Enhanced responsive layout for mobile with safety fallbacks
  const iconSize = useBreakpointValue({ base: 20, sm: 22, md: 24 }) || 22;
  const iconWeight = useBreakpointValue({ base: "regular", md: "light" }) || "regular";
  const sectionSpacing = useBreakpointValue({ base: 6, md: 8 }) || 6;
  const filterColumns = useBreakpointValue({ base: 1, sm: 2, md: 3, lg: 4 }) || 2;
  const cardPadding = useBreakpointValue({ base: 4, md: 6 }) || 4;
  const headerSpacing = useBreakpointValue({ base: 4, md: 6 }) || 4;

  // Adventure Journal theme colors - using proper theme tokens
  const bgColor = useColorModeValue('#F0E6D2', '#2C231B'); // bg.default
  const sectionBg = useColorModeValue('#E6DAC0', '#38291E'); // bg.surface
  const accentColor = useColorModeValue('#C19434', '#D6AD60'); // interactive.primary (gold)
  const cardBg = useColorModeValue('#E6DAC0', '#38291E'); // bg.surface
  const tagBg = useColorModeValue('#D9CAA7', '#483123'); // bg.accent
  const tagActiveBg = useColorModeValue('#C19434', '#D6AD60'); // interactive.primary (gold)
  const tagColor = useColorModeValue('#3B2E1E', '#E6D7B8'); // text.default
  const tagActiveColor = useColorModeValue('white', '#2C231B'); // High contrast
  const filterActiveBg = useColorModeValue('#D9CAA7', '#483123'); // bg.accent
  const filterBoxBg = useColorModeValue('#E6DAC0', '#38291E'); // bg.surface
  const filterBoxBorder = useColorModeValue('#9C826B', '#6A4E33'); // border.default
  const secondaryTextColor = useColorModeValue('#6B573B', '#B7A58B'); // text.muted

  // Reset pagination when media type or filter changes
  useEffect(() => {
    setCurrentPage(0);
  }, [currentMediaType, activeTag, selectedTags]);

  // All unique features for the filter UI with safety check
  const allFeatures = useMemo(() => getAllFeatures(media || []), [media]);

  // Filter media by type and apply filters
  const displayMedia = useMemo(() => {
    if (!media || !Array.isArray(media)) return [];
    
    let filteredItems = [...media];

    // Apply type filter
    if (currentMediaType !== 'all') {
      filteredItems = filteredItems.filter(item => item.type === currentMediaType);
    }

    // Apply feature filters with AND/OR logic
    if (selectedTags.length > 0) {
      if (filters?.featureFilterLogic === 'OR') {
        // OR: media must have at least one of the selected features
        filteredItems = filteredItems.filter(item => {
          return selectedTags.some(tag => mediaHasFeature(item, tag));
        });
      } else {
        // AND: media must have all of the selected features (default)
        filteredItems = filteredItems.filter(item => {
          return selectedTags.every(tag => mediaHasFeature(item, tag));
        });
      }
    }
    // Use parent filteredMedia if provided and no local filters
    else if (filteredMedia && selectedTags.length === 0) {
      return filteredMedia;
    }
    
    // Sort media by type and date
    return filteredItems.sort((a, b) => {
      const typeOrder = { image: 0, video: 1, '3d': 2, article: 3 };
      const typeCompare = (typeOrder[a.type] ?? 999) - (typeOrder[b.type] ?? 999);
      if (typeCompare !== 0) return typeCompare;
      
      // If same type, sort by date descending
      const dateA = a.uploadedAt?.toDate?.() || new Date(a.uploadedAt);
      const dateB = b.uploadedAt?.toDate?.() || new Date(b.uploadedAt);
      return dateB - dateA;
    });
  }, [media, currentMediaType, selectedTags, filteredMedia, filters?.featureFilterLogic]);

  const handleMediaClick = (index) => {
    setSelectedPhotoIndex(index);
    setImageError(false);
    const mediaType = displayMedia[index].type;
    if (mediaType === 'video') {
      setIsVideoModalOpen(true);
    } else if (mediaType === 'article') {
      setIsArticleModalOpen(true);
    } else if (mediaType === '3d') {
      setIsThreeDModalOpen(true);
    } else {
      onOpen();
    }
  };

  const handleNavigate = (direction) => {
    setSelectedPhotoIndex((prev) => {
      if (direction === 'next') {
        return (prev + 1) % displayMedia.length;
      }
      return (prev - 1 + displayMedia.length) % displayMedia.length;
    });
  };
  
  const handleTagClick = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
    setCurrentPage(0);
  };
  
  const handleClearFilters = () => {
    setSelectedTags([]);
    setActiveTag(null);
    setCurrentPage(0);
  };

  const currentMedia = displayMedia[selectedPhotoIndex];
  
  // Format feature label for display
  const formatFeatureLabel = (label) => {
    return String(label)
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const renderMediaModal = () => {
    if (!currentMedia) return null;

    switch (currentMedia.type) {
      case 'image':
        return (
          <ImagePreviewModal
            isOpen={isOpen}
            onClose={onClose}
            imageUrl={currentMedia?.url}
            currentImage={{
              ...currentMedia,
              siteId: site.id,
              siteLocation: site.location?.coordinates,
              subLocation: currentMedia?.subLocation
            }}
            onPrevious={() => handleNavigate('prev')}
            onNext={() => handleNavigate('next')}
            hasMultiple={displayMedia.length > 1}
            onMediaUpdated={onMediaUpdated}
          />
        );
      case 'video':
        return (
          <VideoModal
            isOpen={isVideoModalOpen}
            onClose={() => setIsVideoModalOpen(false)}
            videoUrl={currentMedia?.url}
            videoData={{
              ...currentMedia,
              siteId: site.id
            }}
            hasMultiple={displayMedia.length > 1}
            onPrevious={() => handleNavigate('prev')}
            onNext={() => handleNavigate('next')}
          />
        );
      case 'article':
        return (
          <ArticleModal
            isOpen={isArticleModalOpen}
            onClose={() => setIsArticleModalOpen(false)}
            articleData={{
              ...currentMedia,
              siteId: site.id
            }}
            hasMultiple={displayMedia.length > 1}
            onPrevious={() => handleNavigate('prev')}
            onNext={() => handleNavigate('next')}
          />
        );
      case '3d':
        return (
          <ThreeDViewerModal
            isOpen={isThreeDModalOpen}
            onClose={() => setIsThreeDModalOpen(false)}
            modelUrl={currentMedia?.url}
            modelData={{
              ...currentMedia,
              siteId: site.id
            }}
            hasMultiple={displayMedia.length > 1}
            onPrevious={() => handleNavigate('prev')}
            onNext={() => handleNavigate('next')}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      bg="bg.surface"
      borderRadius="2xl"
      borderWidth="1px"
      borderColor="border.light"
      overflow="hidden"
      boxShadow="xl"
      _hover={{
        boxShadow: "2xl",
        transform: { base: "none", md: "translateY(-2px)" }, // Disable hover transform on mobile
        transition: "all 0.3s ease"
      }}
      transition="all 0.3s ease"
      className="media-gallery-container"
    >
      {/* Header */}
      <Box
        bg="linear-gradient(135deg, interactive.primary 0%, accent.gold.600 100%)"
        px={{ base: 6, md: 8 }}
        py={5}
        borderBottom="1px solid"
        borderColor="border.light"
      >
        <Flex align="center" justify="space-between">
          <HStack spacing={4}>
            <Box
              bg="whiteAlpha.200"
              borderRadius="full"
              p={3}
              boxShadow="md"
            >
              <Images size={iconSize} weight="duotone" color="white" />
            </Box>
            <Box>
              <Heading 
                size={{ base: "lg", md: "xl" }}
                fontFamily="heading"
                fontWeight="bold"
                color="white"
                textShadow="0 1px 2px rgba(0,0,0,0.1)"
                mb={1}
              >
                Media Gallery
              </Heading>
              <Text
                fontSize="sm"
                color="whiteAlpha.900"
                fontFamily="body"
              >
                Explore archaeological findings and documentation
              </Text>
            </Box>
          </HStack>
          
          {/* Add Media Button */}
          {user && (
            <Tooltip label="Add Media" placement="bottom" hasArrow>
              <IconButton
                icon={<Plus weight="bold" />}
                onClick={() => setIsUploadModalOpen(true)}
                bg="whiteAlpha.200"
                color="white"
                size="lg"
                borderRadius="full"
                minW="48px"
                minH="48px"
                _hover={{
                  bg: "whiteAlpha.300",
                  transform: 'translateY(-2px)',
                  boxShadow: 'xl'
                }}
                _active={{
                  transform: 'translateY(0)'
                }}
                boxShadow="lg"
                transition="all 0.2s ease-in-out"
                aria-label="Add Media"
                border="1px solid"
                borderColor="whiteAlpha.300"
              />
            </Tooltip>
          )}
        </Flex>
      </Box>
      
      {/* Content */}
      <Box px={{ base: 6, md: 8 }} py={{ base: 6, md: 8 }}>
        <VStack spacing={{ base: 6, md: 8 }} align="stretch">
          <MediaGalleryHeader
            mediaCount={displayMedia.length}
            showThumbnailTags={showThumbnailTags}
            onThumbnailTagsToggle={setShowThumbnailTags}
            onMediaTypeChange={setCurrentMediaType}
            currentMediaType={currentMediaType}
            accentColor={accentColor}
          />
          
          {/* Enhanced Feature Filters - Desktop Only */}
          {allFeatures.length > 0 && (
            <Box
              p={{ base: 4, md: 5 }}
              borderRadius="xl"
              bg={filterBoxBg}
              borderWidth="1px"
              borderColor={filterBoxBorder}
              boxShadow="md"
              display={{ base: "none", md: "block" }} // Hide on mobile
            >
              {/* Filter Header */}
              <Flex
                align="center"
                gap={3}
                mb={4}
                justify="space-between"
                flexWrap="wrap"
              >
                <HStack spacing={3}>
                  <Icon as={FunnelSimple} color={accentColor} weight="duotone" boxSize={iconSize} />
                  <Heading
                    size="md"
                    fontFamily="Lora"
                    fontWeight="semibold"
                    color={tagColor}
                  >
                    Feature Filters
                  </Heading>
                </HStack>
                
                {selectedTags.length > 0 && (
                  <Button 
                    size={{ base: "md", md: "sm" }}
                    variant="outline"
                    leftIcon={<X weight="bold" />}
                    onClick={handleClearFilters}
                    color={tagColor}
                    borderColor={filterBoxBorder}
                    bg="transparent"
                    _hover={{
                      bg: tagBg,
                      borderColor: accentColor
                    }}
                    minH="40px" // Touch target
                    fontFamily="Inter"
                  >
                    Clear ({selectedTags.length})
                  </Button>
                )}
              </Flex>

              {/* Filter Logic Controls - Enhanced for mobile */}
              {(selectedTags.length > 1 || (filters?.features && filters.features.length > 1)) && (
                <Box mb={4}>
                  <VStack spacing={3} align="stretch">
                    <Flex
                      direction={{ base: 'column', sm: 'row' }}
                      align={{ base: 'stretch', sm: 'center' }}
                      justify="space-between"
                      gap={3}
                    >
                      <HStack spacing={3}>
                        <Text fontSize="md" color={tagColor} fontWeight="medium" fontFamily="Inter">
                          Filter Logic:
                        </Text>
                        <ButtonGroup isAttached variant="outline" size={{ base: "md", md: "sm" }}>
                          <Button
                            onClick={() => onFiltersChange?.({ ...filters, featureFilterLogic: 'AND' })}
                            bg={filters?.featureFilterLogic !== 'OR' ? tagActiveBg : 'transparent'}
                            color={filters?.featureFilterLogic !== 'OR' ? tagActiveColor : tagColor}
                            borderColor={filterBoxBorder}
                            minH="40px"
                            fontFamily="Inter"
                            fontWeight="semibold"
                            _hover={{
                              bg: filters?.featureFilterLogic !== 'OR' ? tagActiveBg : tagBg,
                              borderColor: accentColor
                            }}
                          >
                            AND
                          </Button>
                          <Button
                            onClick={() => onFiltersChange?.({ ...filters, featureFilterLogic: 'OR' })}
                            bg={filters?.featureFilterLogic === 'OR' ? tagActiveBg : 'transparent'}
                            color={filters?.featureFilterLogic === 'OR' ? tagActiveColor : tagColor}
                            borderColor={filterBoxBorder}
                            minH="40px"
                            fontFamily="Inter"
                            fontWeight="semibold"
                            _hover={{
                              bg: filters?.featureFilterLogic === 'OR' ? tagActiveBg : tagBg,
                              borderColor: accentColor
                            }}
                          >
                            OR
                          </Button>
                        </ButtonGroup>
                      </HStack>
                      
                      <Button
                        size={{ base: "md", md: "sm" }}
                        variant="ghost"
                        leftIcon={<InfoIcon weight="duotone" />}
                        onClick={() => setIsInfoModalOpen(true)}
                        color={tagColor}
                        _hover={{ bg: tagBg }}
                        minH="40px"
                        fontFamily="Inter"
                      >
                        Info
                      </Button>
                    </Flex>
                    
                    <Text fontSize="sm" color={secondaryTextColor} fontFamily="Inter" lineHeight="1.4">
                      {filters?.featureFilterLogic === 'OR' 
                        ? '✨ Show media with ANY of the selected features'
                        : '🎯 Show media with ALL of the selected features (default)'
                      }
                    </Text>
                  </VStack>
                </Box>
              )}
              
              {/* Active Filters Display - Enhanced */}
              {selectedTags.length > 0 && (
                <Box
                  mb={5}
                  p={4}
                  borderRadius="lg"
                  bg={useColorModeValue('#EAE0D1', '#4F4B42')}
                  borderWidth="1px"
                  borderColor={filterBoxBorder}
                >
                  <Flex align="center" mb={3}>
                    <Icon as={CheckCircle} color={accentColor} mr={2} boxSize={5} />
                    <Text
                      fontSize="md"
                      fontWeight="semibold"
                      fontFamily="Lora"
                      color={tagColor}
                    >
                      Active Filters
                    </Text>
                  </Flex>
                  
                  <Wrap spacing={3}>
                    {selectedTags.map(tag => {
                      const feature = allFeatures.find(f => f.id === tag);
                      return (
                        <WrapItem key={tag}>
                          <Tag
                            size="lg"
                            bg={tagActiveBg}
                            color={tagActiveColor}
                            borderRadius="full"
                            minH="36px"
                            px={4}
                            py={2}
                            fontFamily="Inter"
                            fontWeight="medium"
                            _hover={{
                              transform: 'translateY(-1px)',
                              boxShadow: 'md'
                            }}
                            transition="all 0.2s ease-in-out"
                          >
                            <TagLabel fontSize="sm">
                              {formatFeatureLabel(feature?.label || tag)}
                            </TagLabel>
                            <TagCloseButton
                              onClick={() => setSelectedTags(selectedTags.filter(t => t !== tag))}
                              _hover={{ bg: 'whiteAlpha.300' }}
                            />
                          </Tag>
                        </WrapItem>
                      );
                    })}
                  </Wrap>
                </Box>
              )}
              
              {/* Feature Tags Grid - Mobile Optimized */}
              <SimpleGrid 
                columns={{ base: 2, sm: 3, md: 4, lg: 5 }} 
                spacing={2}
              >
                {allFeatures.slice(0, 20).map(feature => (
                  <Box key={feature.id}>
                    <Tooltip 
                      label={`${feature.count} media items have this feature`}
                      hasArrow
                      placement="top"
                      bg={tagColor}
                      color={filterBoxBg}
                      borderRadius="md"
                      px={2}
                      py={1}
                      fontSize="xs"
                    >
                      <Tag
                        size="sm"
                        minH="32px" // Smaller touch target
                        py={1}
                        px={2}
                        width="100%"
                        bg={selectedTags.includes(feature.id) ? tagActiveBg : "transparent"}
                        color={selectedTags.includes(feature.id) ? tagActiveColor : tagColor}
                        borderWidth="1px"
                        borderColor={selectedTags.includes(feature.id) ? tagActiveBg : filterBoxBorder}
                        cursor="pointer"
                        onClick={() => handleTagClick(feature.id)}
                        fontFamily="Inter"
                        fontWeight="medium"
                        _hover={{
                          bg: selectedTags.includes(feature.id) ? tagActiveBg : tagBg,
                          transform: 'translateY(-1px)',
                          boxShadow: 'sm',
                          borderColor: accentColor
                        }}
                        _active={{
                          transform: 'translateY(0)'
                        }}
                        transition="all 0.2s ease-in-out"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        borderRadius="md"
                      >
                        <TagLabel fontSize="xs" noOfLines={1}>
                          {formatFeatureLabel(feature.label)}
                        </TagLabel>
                        <Badge
                          borderRadius="full"
                          bg={selectedTags.includes(feature.id) ? "whiteAlpha.300" : tagActiveBg}
                          color={selectedTags.includes(feature.id) ? tagActiveColor : "white"}
                          ml={1}
                          px={1.5}
                          py={0.5}
                          fontSize="2xs"
                          fontWeight="bold"
                        >
                          {feature.count}
                        </Badge>
                      </Tag>
                    </Tooltip>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          )}

          {/* Media grid section */}
          <Box
            bg={sectionBg}
            p={{ base: 3, md: 5 }}
            borderRadius="lg"
            boxShadow="sm"
            borderWidth="1px"
            borderColor={filterBoxBorder}
          >
            {/* Media Count Summary */}
            {displayMedia.length > 0 && (
              <Flex 
                justify="space-between" 
                mb={4} 
                align="center"
                flexWrap="wrap"
                gap={2}
              >
                <Badge
                  bg={tagActiveBg}
                  color={tagActiveColor}
                  px={3}
                  py={2}
                  borderRadius="full"
                  fontWeight="semibold"
                  fontSize="sm"
                  fontFamily="Inter"
                  boxShadow="sm"
                >
                  Showing {displayMedia.length} {displayMedia.length === 1 ? 'item' : 'items'}
                </Badge>

                {selectedTags.length > 0 && (
                  <Text
                    fontSize="sm"
                    color={secondaryTextColor}
                    fontFamily="Inter"
                    fontWeight="medium"
                  >
                    {selectedTags.length} {selectedTags.length === 1 ? 'filter' : 'filters'} applied
                  </Text>
                )}
              </Flex>
            )}
            
            <MediaGalleryGrid
              media={displayMedia}
              onMediaClick={handleMediaClick}
              showThumbnailTags={showThumbnailTags}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onTagClick={tag => {
                const feature = allFeatures.find(f => f.label === tag);
                if (feature) {
                  handleTagClick(feature.id);
                }
              }}
              activeTag={activeTag}
            />
          </Box>

          {renderMediaModal()}

          {/* Upload Modal */}
          <Modal 
            isOpen={isUploadModalOpen} 
            onClose={() => setIsUploadModalOpen(false)} 
            size="full"
            isCentered={false}
          >
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
            <ModalContent
              bg={cardBg}
              borderColor={{ base: "transparent", md: filterBoxBorder }}
              borderWidth={{ base: 0, md: "1px" }}
              borderRadius={{ base: 0, md: "xl" }}
              boxShadow={{ base: "none", md: "xl" }}
              mx={0} /* Remove horizontal margins completely */
              my={{ base: 0, md: 4 }}
              maxH="100vh"
              minH="100vh"
              w="100vw"
              maxW="100vw"
              position="relative"
            >
              <ModalHeader
                fontFamily="Lora"
                fontWeight="semibold"
                color={tagColor}
                borderBottomWidth="1px"
                borderBottomColor={filterBoxBorder}
                pb={3}
                px={{ base: 3, md: 6 }} /* Reduced padding on mobile */
                py={{ base: 3, md: 6 }} /* Reduced padding on mobile */
              >
                <Flex align="center" gap={3}>
                  <Plus size={20} weight="duotone" color={accentColor} />
                  Add Media
                </Flex>
              </ModalHeader>
              <ModalCloseButton 
                color={tagColor} 
                size="lg"
                top={{ base: 3, md: 6 }}
                right={{ base: 3, md: 6 }}
              />
              <ModalBody 
                px={{ base: 0, md: 6 }} /* Remove horizontal padding on mobile */
                py={{ base: 2, md: 6 }} /* Reduced vertical padding on mobile */
                pb={{ base: 4, md: 6 }} /* Reduced bottom padding on mobile */
                overflow="auto"
                flex="1"
              >
                <MediaGalleryUpload
                  siteId={site.id}
                  onUploadComplete={(data) => {
                    onMediaUploadComplete(data);
                    setIsUploadModalOpen(false);
                  }}
                  media={media}
                  isMobile={useBreakpointValue({ base: true, md: false }) || false} /* Pass isMobile prop with fallback */
                />
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Feature Filter Logic Info Modal */}
          <Modal isOpen={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)} isCentered motionPreset="slideInBottom">
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
            <ModalContent
              bg={useColorModeValue('white', 'gray.800')}
              color={useColorModeValue('gray.800', 'whiteAlpha.900')}
              borderRadius="xl"
              border="1px solid"
              borderColor={useColorModeValue('gray.200', 'gray.700')}
            >
              <ModalHeader fontFamily="heading" fontWeight="bold" color={useColorModeValue('gray.700', 'white')}>
                Feature Filtering Logic
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <Text color={useColorModeValue('gray.700', 'whiteAlpha.800')}>
                    When you select multiple features to filter by, you can choose how they are combined:
                  </Text>
                  <Box
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor={useColorModeValue('gray.200', 'gray.600')}
                    bg={useColorModeValue('gray.50', 'gray.700')}
                  >
                    <Heading size="sm" mb={1} color={useColorModeValue('blue.600', 'blue.300')}>
                      AND Logic (Default)
                    </Heading>
                    <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.300')}>
                      Shows media items that contain <Text as="strong" color={useColorModeValue('gray.700', 'gray.100')}>ALL</Text> of the selected features. This is more restrictive and helps you find very specific results.
                    </Text>
                  </Box>
                  <Box
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor={useColorModeValue('gray.200', 'gray.600')}
                    bg={useColorModeValue('gray.50', 'gray.700')}
                  >
                    <Heading size="sm" mb={1} color={useColorModeValue('blue.600', 'blue.300')}>
                      OR Logic
                    </Heading>
                    <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.300')}>
                      Shows media items that contain <Text as="strong" color={useColorModeValue('gray.700', 'gray.100')}>ANY</Text> of the selected features. This is less restrictive and broadens your search.
                    </Text>
                  </Box>
                  <Text fontSize="xs" color="text.muted">
                    This logic applies to the features found within individual photos, videos, or 3D models.
                  </Text>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button
                  variant="outline"
                  color={accentColor}
                  borderColor={filterBoxBorder}
                  onClick={() => setIsInfoModalOpen(false)}
                  _hover={{ bg: 'bg.accent' }}
                >
                  Got it
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </VStack>
      </Box>

      {/* Mobile Filters Component - Integrated into the component */}
      <Box mt={4} px={3}>
        <MobileMediaFilters
          allFeatures={allFeatures}
          selectedTags={selectedTags}
          filters={filters}
          onTagClick={handleTagClick}
          onClearFilters={handleClearFilters}
          onFiltersChange={onFiltersChange}
          formatFeatureLabel={formatFeatureLabel}
          isVisible={true} /* Enabled for mobile filtering */
          isIntegrated={true} /* New prop to indicate integration with component */
        />
      </Box>
    </Box>
  );
}
